import { CreditUsageType } from '@aibees/shared/definition';
import { fCredit } from '@aibees/shared/utils';
import { PersonUpdateBreakdownKey } from '../hooks/types';

// Reads a rate from the bootstrap cost map. Prices are server-driven, so no
// tooltip may hardcode one.
export type GetCreditCost = (type: CreditUsageType) => number;

// Customer-facing row title for each cost-estimation breakdown key.
export const PERSON_UPDATE_BREAKDOWN_TITLE: Record<
  PersonUpdateBreakdownKey,
  string
> = {
  [PersonUpdateBreakdownKey.WORK_HISTORY]: 'Work History / Job Change',
  [PersonUpdateBreakdownKey.PROFILE_INSIGHTS]: 'Profile Insights',
  [PersonUpdateBreakdownKey.PERSONALITY_ANALYSIS]: 'Personality Analysis',
};

// The cost-estimation response carries no per-row rate, so rows read theirs from
// the bootstrap cost map.
export const PERSON_UPDATE_BREAKDOWN_CREDIT_TYPE: Record<
  PersonUpdateBreakdownKey,
  CreditUsageType
> = {
  [PersonUpdateBreakdownKey.WORK_HISTORY]: CreditUsageType.CONTACT_JOB_CHANGE,
  [PersonUpdateBreakdownKey.PROFILE_INSIGHTS]:
    CreditUsageType.CONTACT_PROFILE_UPDATE,
  [PersonUpdateBreakdownKey.PERSONALITY_ANALYSIS]:
    CreditUsageType.CONTACT_ANALYSIS_UPDATE,
};

/**
 * Info-icon copy for a breakdown key, shared by the Enrich menu item and the
 * matching row in every preflight dialog — the same update means the same thing
 * in both places, so the copy lives here rather than in either component.
 *
 * Partial: Personality Analysis has no approved copy while it is descoped, and a
 * row without an entry simply renders no icon.
 */
export const PERSON_UPDATE_BREAKDOWN_TOOLTIP: Partial<
  Record<PersonUpdateBreakdownKey, (getCost: GetCreditCost) => string>
> = {
  [PersonUpdateBreakdownKey.WORK_HISTORY]: (getCost) =>
    `Updates Work History changes, including a Top Current Company change, title or date changes, and secondary or past positions. Other Profile Insights are not included. ${fCredit(
      getCost(CreditUsageType.CONTACT_JOB_CHANGE)
    )} credits once per successfully updated latest profile version.`,
  [PersonUpdateBreakdownKey.PROFILE_INSIGHTS]: (getCost) =>
    `Updates previously unlocked Profile Insights sections that changed, such as Education, Certifications, Publications, Volunteer Experience, Projects and Languages. Work History / Job Change is not included. Never-unlocked or current sections are skipped. ${fCredit(
      getCost(CreditUsageType.CONTACT_PROFILE_UPDATE)
    )} credits once per successfully updated latest profile version.`,
};
